import React, { useState } from 'react';
import Alert from '@mui/material/Alert';
import styles from './VideoStyles.module.css';

import { ProgressBar, Spinner } from '../../components/Progress';
import MainLayout from '../../layouts/MainLayout';

import apiClient from '../../api/apiClient';
import {
  handleDownloadTranscription,
  handleDownloadEmotions,
  handleDownloadSentimentCoding,
} from './DownloadAnalysisPage';
import checkStatus from '../../hooks/checkAnalysisStatus';
import runProgressStep from '../../hooks/runProgressStep';

const client = apiClient('transcription');
const client_fer = apiClient('emotions');
const client_sentiment = apiClient('sentiment');

const TestTranscription = () => {
  const [runTranscription, setRunTranscription] = useState(true);
  const [runEmotionAnalysis, setRunEmotionAnalysis] = useState(true);
  const [runSentimentAnalysis, setRunSentimentAnalysis] = useState(true);
  const [runLLMAnalysis, setRunLLMAnalysis] = useState(true);

  const [startupName, setStartupName] = useState('');
  const [file, setFile] = useState(null);

  const [uploading, setUploading] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState('');
  const [errors, setError] = useState('');

  const [ferRunning, setFerRunning] = useState(false);
  const [ferProgress, setFerProgress] = useState(0);

  const [sentimentCodingRunning, setSentimentCodingRunning] = useState(false);
  const [sentimentCodingProgress, setsentimentCodingProgress] = useState(0);

  const [llmAnalysisRunning, setLLMAnalysisRunning] = useState(false);
  const [llmAnalysisProgress, setLLMAnalysisProgress] = useState(0);

  const [discussionDuration, setDiscussionDuration] = useState(15 * 60); // in seconds

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!startupName) {
      setError('Please select a startup.');
      return;
    }

    if (!file) {
      setError(
        'Please provide at least the discussion video of the respondent.'
      );
      return;
    }

    setUploading(true);
    setResult('');

    const formData = new FormData();
    formData.append('startup_name', startupName);
    formData.append('respondent_discussion', file);
    // Add flags if transcription is requested
    formData.append('run_transcription', runTranscription);
    formData.append('run_emotion', runEmotionAnalysis);
    formData.append('run_sentiment', runSentimentAnalysis);
    formData.append('run_llm_analysis', runLLMAnalysis);

    try {
      console.log('llm:', runLLMAnalysis);
      console.log('sentiment:', runSentimentAnalysis);
      console.log('emotion:', runEmotionAnalysis);
      await client.post('/upload/video', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      await new Promise((resolve) => setTimeout(resolve, 2000));
      setUploading(false);

      var statusResp;

      if (runTranscription) {
        statusResp = await client.get(`/status/${startupName}`, {
          validateStatus: () => true,
        });
      } else {
        statusResp = await client_fer.get(`/status/${startupName}`, {
          validateStatus: () => true,
        });
      }

      if (
        statusResp.status === 202 &&
        statusResp.data.status?.includes('in_progress')
      ) {
        if (runTranscription) {
          startTranscriptionStep();
        } else {
          startFERStep();
        }
      } else {
        setError(
          'Unexpected status: ' +
            (statusResp.data.status || 'No status') +
            ' with error: ' +
            (statusResp.data.detail || 'No error')
        );
      }
    } catch (error) {
      setUploading(false);
      setError('Upload error: ' + error.message);
    }
  };

  const startTranscriptionStep = () => {
    runProgressStep({
      setRunning: setTranscribing,
      setProgress: setProgress,
      fallbackDurationMs: 15 * 60 * 1000,
      estimatedDurationSeconds: discussionDuration,
      onComplete: checkTranscriptionStatus,
    });
  };

  const startSentimentCodingStep = () => {
    runProgressStep({
      setRunning: setSentimentCodingRunning,
      setProgress: setsentimentCodingProgress,
      fallbackDurationMs: 30 * 1000,
      estimatedDurationSeconds: 30,
      onComplete: checkSentimentCodingStatus,
      scale: 1.0,
    });
  };

  const startLLMAnalysisStep = () => {
    runProgressStep({
      setRunning: setLLMAnalysisRunning,
      setProgress: setLLMAnalysisProgress,
      fallbackDurationMs: 15 * 60 * 1000,
      estimatedDurationSeconds: discussionDuration,
      onComplete: checkLLMAnalysisStatus,
      scale: 0.6,
    });
  };

  const startFERStep = () => {
    runProgressStep({
      setRunning: setFerRunning,
      setProgress: setFerProgress,
      fallbackDurationMs: 15 * 60 * 1000,
      estimatedDurationSeconds: discussionDuration,
      onComplete: checkEmotionsStatus,
      scale: 0.2,
    });
  };

  const checkTranscriptionStatus = () => {
    checkStatus({
      client,
      endpoint: `/status/${startupName}`,
      setRunning: setTranscribing,
      setError,
      onSuccess: () => setResult('Analysis completed successfully!'),
      nextStep: runSentimentAnalysis
        ? startSentimentCodingStep
        : runLLMAnalysis
          ? startLLMAnalysisStep
          : runEmotionAnalysis
            ? startFERStep
            : null,
      pollingLabel: 'Transcription',
    });
  };

  const checkSentimentCodingStatus = () => {
    checkStatus({
      client: client_sentiment,
      endpoint: `/status/${startupName}`,
      setRunning: setSentimentCodingRunning,
      setError,
      onSuccess: () => setResult('Analysis completed successfully!'),
      nextStep: runLLMAnalysis
        ? startLLMAnalysisStep
        : runEmotionAnalysis
          ? startFERStep
          : null,
      pollingLabel: 'Sentiment Coding',
    });
  };

  const checkLLMAnalysisStatus = () => {
    checkStatus({
      client: client_sentiment,
      endpoint: `/status/${startupName}`,
      setRunning: setLLMAnalysisRunning,
      setError,
      onSuccess: () => setResult('Analysis completed successfully!'),
      nextStep: runEmotionAnalysis ? startFERStep : null,
      pollingLabel: 'LLM Analysis',
    });
  };

  const checkEmotionsStatus = () => {
    checkStatus({
      client: client_fer,
      endpoint: `/status/${startupName}`,
      setRunning: setFerRunning,
      setError,
      onSuccess: () => setResult('Analysis should now be completed!'),
      pollingLabel: 'Emotions Analysis',
    });
  };

  return (
    <MainLayout>
      <div style={{ margin: '20px' }}>
        <h1>Upload Interview Video Files</h1>
        <form onSubmit={handleSubmit}>
          <label>Startup Name:</label>
          <select
            value={startupName}
            onChange={(e) => setStartupName(e.target.value)}
            required
            disabled={uploading || transcribing}
          >
            <option value="">-- Select a Startup --</option>
            <option value="hungry_dog">hungry_dog</option>
            <option value="golden_cat">golden_cat</option>
            <option value="grumpy_bird">grumpy_bird</option>
            <option value="test">test</option>
          </select>

          <br />
          <br />

          <div style={{ marginTop: '20px' }}>
            <h3>Select Processing Steps:</h3>

            <label>
              <input
                type="checkbox"
                checked={runTranscription}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setRunTranscription(checked);
                  if (!checked) {
                    setRunSentimentAnalysis(false); // auto-disable sentiment
                    setRunLLMAnalysis(false); // auto-disable LLM analysis
                  }
                }}
                disabled={uploading || transcribing}
              />
              Transcription
            </label>
            <br />

            <label>
              <input
                type="checkbox"
                checked={runSentimentAnalysis}
                onChange={(e) => setRunSentimentAnalysis(e.target.checked)}
                disabled={!runTranscription || uploading || transcribing}
              />
              Sentiment Analysis
            </label>
            <br />

            <label>
              <input
                type="checkbox"
                checked={runLLMAnalysis}
                onChange={(e) => setRunLLMAnalysis(e.target.checked)}
                disabled={!runTranscription || uploading || transcribing}
              />
              LLM Analysis
            </label>
            <br />

            <label>
              <input
                type="checkbox"
                checked={runEmotionAnalysis}
                onChange={(e) => setRunEmotionAnalysis(e.target.checked)}
                disabled={uploading || transcribing}
              />
              Emotion Analysis
            </label>

            {!runTranscription && (
              <Alert severity="info">
                Sentiment and LLM analysis requires transcription. Enable
                transcription to use these steps.
              </Alert>
            )}
          </div>

          <br />
          <br />

          <div className={styles.uploadSections}>
            <div className={styles.uploadBox}>
              <h2>Video Upload</h2>
              <div className={styles.uploadRow}>
                <label>Select Video:</label>
                <input
                  type="file"
                  accept="video/*"
                  required
                  onChange={(e) => setFile(e.target.files[0])}
                  disabled={uploading || transcribing}
                />
              </div>
            </div>
          </div>

          <button type="submit" disabled={uploading || transcribing}>
            Upload Video
          </button>
        </form>

        {uploading && (
          <p>
            Uploading... <Spinner />
          </p>
        )}

        {transcribing && (
          <div>
            <p>
              Transcription in progress...
              {discussionDuration
                ? ` approx. ${Math.ceil(discussionDuration / 60)} min`
                : ' approx. 15 min'}
            </p>
            <ProgressBar percentage={progress} />
          </div>
        )}

        {sentimentCodingRunning && (
          <div>
            <p>✅ Transcription</p>
            <p>
              Sentiment Coding in progress...
              {discussionDuration
                ? ` approx. ${Math.ceil(discussionDuration / 60)} min`
                : ' approx. 15 min'}
            </p>
            <ProgressBar percentage={sentimentCodingProgress} />
          </div>
        )}

        {llmAnalysisRunning && (
          <div>
            <p>✅ Transcription</p>
            {runSentimentAnalysis && <p>✅ Sentiment Coding</p>}
            <p>
              LLM Analysis in progress...
              {discussionDuration
                ? ` approx. ${Math.ceil(discussionDuration / 60)} min`
                : ' approx. 15 min'}
            </p>
            <ProgressBar percentage={llmAnalysisProgress} />
          </div>
        )}

        {ferRunning && (
          <div>
            {runTranscription && <p>✅ Transcription</p>}
            {runSentimentAnalysis && <p>✅ Sentiment Coding</p>}
            {runLLMAnalysis && <p>✅ LLM Analysis</p>}
            <p>
              Facial Emotion Recognition in progress...
              {discussionDuration
                ? ` approx. ${Math.ceil(discussionDuration / 60)} min`
                : ' approx. 15 min'}
            </p>
            <ProgressBar percentage={ferProgress} />
          </div>
        )}

        {result &&
          !ferRunning &&
          !transcribing &&
          !sentimentCodingRunning &&
          !llmAnalysisRunning && (
            <div>
              <h2>Analysis Complete:</h2>
              <p>{result}</p>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {runTranscription && (
                  <button
                    onClick={() => handleDownloadTranscription(startupName)}
                  >
                    Download Transcription
                  </button>
                )}
                {runEmotionAnalysis && (
                  <button onClick={() => handleDownloadEmotions(startupName)}>
                    Download Emotions Analysis
                  </button>
                )}
                {runSentimentAnalysis && (
                  <button
                    onClick={() => handleDownloadSentimentCoding(startupName)}
                  >
                    Download Sentiment Analysis
                  </button>
                )}
              </div>
            </div>
          )}
        {errors && (
          <div>
            <Alert severity="error">{errors}</Alert>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default TestTranscription;
